import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import type { CustomerSendInput } from '@super-mega/server/contracts/bill'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import { useTRPC } from '@/trpc/react.ts'

export function useCustomerSendSubmission() {
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message } = App.useApp()
  const send = useMutation(trpc.bill.sendCustomer.mutationOptions())
  const pendingRequest = useRef<CustomerSendInput | undefined>(undefined)
  const sending = useRef(false)
  const [canRetry, setCanRetry] = useState(false)

  const submit = async (input: CustomerSendInput) => {
    if (sending.current) return
    sending.current = true
    pendingRequest.current = input
    try {
      await send.mutateAsync(input)
    } catch (error) {
      // 业务冲突重新预览；网络结果不确定时保留同一幂等请求，避免重复组单。
      if (
        error instanceof Error &&
        'data' in error &&
        (error.data as { code?: string } | undefined)?.code === 'CONFLICT'
      ) {
        pendingRequest.current = undefined
      }
      setCanRetry(!!pendingRequest.current)
      sending.current = false
      return
    }
    pendingRequest.current = undefined
    sending.current = false
    await Promise.all([
      refreshMoneyQueries(cache, trpc),
      cache.invalidateQueries(trpc.bot.wecomDeliveries.queryFilter({ botId: input.botId })),
      cache.invalidateQueries(trpc.bot.wecomConnection.queryFilter({ botId: input.botId }))
    ]).catch(() => {
      void message.warning('发送任务已提交，列表刷新失败，请手动刷新')
    })
  }
  const retry = () => {
    if (pendingRequest.current) void submit(pendingRequest.current)
    else send.reset()
  }
  return { send, submit, retry, canRetry }
}
